import { useCallback, useEffect, useRef, useState } from 'react';
import { clampRect, newSelectionId, rectFromDrag, selectionCorners } from './photoSplitEngine';

const HANDLE = 7;
/** A drag shorter than this was a click, not a new selection. */
const MIN_DRAG = 6;
/** Tallest the picture's window may get, so magnifying scrolls it, not the page. */
const VIEWPORT_MAX = 620;
/** How far above the top edge the rotate knob sits, in screen pixels. */
const KNOB_LIFT = 26;

/**
 * The picture, with the pieces somebody has marked on it.
 *
 * Drag on open picture to mark a piece; drag inside one to move it; drag a
 * corner to resize; drag the knob above it to turn it. A photo lying at an
 * angle on a table is the normal case rather than the exception, so turning a
 * selection is part of marking it, not a second pass afterwards.
 */
export default function PhotoSplitStage({
    image, selections, selectedId, onSelect, onChange, zoom = 1, panMode = false,
}) {
    const wrapRef = useRef(null);
    const canvasRef = useRef(null);
    const dragRef = useRef(null);
    const [box, setBox] = useState({ width: 0, height: 0, scale: 1 });
    const [draft, setDraft] = useState(null);
    // What the pointer is over. A handle that does not say what it does until
    // you have already dragged it is a handle nobody trusts.
    const [hover, setHover] = useState(null);

    const NW = image?.naturalWidth || 0;
    const NH = image?.naturalHeight || 0;

    useEffect(() => {
        const el = wrapRef.current;
        if (!el || !NW) return undefined;
        const measure = () => {
            // The picture fits the panel at zoom 1; past that it overflows and
            // the box below scrolls, which is what magnifying is for.
            // Fit is measured against the window, not the scroll box: the box
            // is the viewport, and its own height is the cap below.
            const view = Math.min(window.innerHeight * 0.62, VIEWPORT_MAX);
            const fit = Math.min(1, el.clientWidth / NW, view / NH);
            const scale = fit * zoom;
            setBox({ width: NW * scale, height: NH * scale, scale });
        };
        measure();
        const ro = new ResizeObserver(measure);
        ro.observe(el);
        window.addEventListener('resize', measure);
        return () => { ro.disconnect(); window.removeEventListener('resize', measure); };
    }, [NW, NH, zoom]);

    /* ------------------------------------------------------------- drawing */

    useEffect(() => {
        const canvas = canvasRef.current;
        if (!canvas || !image || !box.width) return;
        const dpr = Math.min(2, window.devicePixelRatio || 1);
        canvas.width = Math.round(box.width * dpr);
        canvas.height = Math.round(box.height * dpr);
        const ctx = canvas.getContext('2d');
        ctx.setTransform(dpr * box.scale, 0, 0, dpr * box.scale, 0, 0);
        ctx.drawImage(image, 0, 0);

        const trace = (sel) => {
            const pts = selectionCorners(sel);
            ctx.beginPath();
            pts.forEach(([x, y], i) => (i ? ctx.lineTo(x, y) : ctx.moveTo(x, y)));
            ctx.closePath();
        };

        // Everything outside the marks is dimmed, so what will be cut is what
        // is lit — the way every crop tool says it.
        if (selections.length) {
            ctx.fillStyle = 'rgba(28,24,21,0.5)';
            ctx.fillRect(0, 0, NW, NH);
            ctx.globalCompositeOperation = 'destination-out';
            for (const s of selections) { trace(s); ctx.fill(); }
            ctx.globalCompositeOperation = 'source-over';
        }

        const line = 2 / box.scale;
        selections.forEach((sel, i) => {
            const on = sel.id === selectedId;
            const pts = selectionCorners(sel);
            ctx.lineWidth = on ? line * 1.6 : line;
            ctx.strokeStyle = on ? '#E0492B' : 'rgba(255,255,255,0.95)';
            trace(sel);
            ctx.stroke();

            const r = 12 / box.scale;
            const [bx, by] = pts[0];
            ctx.fillStyle = on ? '#E0492B' : 'rgba(28,24,21,0.72)';
            ctx.beginPath();
            ctx.arc(bx + r * 1.1, by + r * 1.1, r, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#fff';
            ctx.font = `600 ${14 / box.scale}px system-ui, sans-serif`;
            ctx.textAlign = 'center';
            ctx.textBaseline = 'middle';
            ctx.fillText(String(i + 1), bx + r * 1.1, by + r * 1.1);

            if (!on) return;
            // The turn knob, on a stalk out of the top edge's middle, which is
            // where every canvas app puts it.
            const [kx, ky] = knobPoint(sel, box.scale);
            const midTop = [(pts[0][0] + pts[1][0]) / 2, (pts[0][1] + pts[1][1]) / 2];
            ctx.strokeStyle = '#E0492B';
            ctx.lineWidth = line;
            ctx.beginPath();
            ctx.moveTo(midTop[0], midTop[1]);
            ctx.lineTo(kx, ky);
            ctx.stroke();

            ctx.fillStyle = '#fff';
            for (const [x, y] of [...pts, [kx, ky]]) {
                ctx.beginPath();
                ctx.arc(x, y, HANDLE / box.scale, 0, Math.PI * 2);
                ctx.fill();
                ctx.stroke();
            }
        });

        if (draft && draft.w > 1 && draft.h > 1) {
            ctx.setLineDash([6 / box.scale, 4 / box.scale]);
            ctx.strokeStyle = '#E0492B';
            ctx.lineWidth = line;
            ctx.strokeRect(draft.x, draft.y, draft.w, draft.h);
            ctx.setLineDash([]);
        }
    }, [image, selections, selectedId, box, draft, NW, NH]);

    /* --------------------------------------------------------- interaction */

    const pointAt = useCallback((e) => {
        const rect = canvasRef.current.getBoundingClientRect();
        return {
            x: (e.clientX - rect.left) / box.scale,
            y: (e.clientY - rect.top) / box.scale,
        };
    }, [box.scale]);

    const onPointerDown = (e) => {
        if (!image) return;
        e.currentTarget.setPointerCapture(e.pointerId);
        // Pan mode moves the view; every other mode marks, moves or resizes a
        // piece. Making this implicit on zoom was wrong: magnified is exactly
        // when somebody wants to mark a piece precisely, and they could not.
        // Handles still win, so a box can be adjusted without leaving pan mode.
        if (panMode && !hitsAnything(selections, pointAt(e), box.scale)) {
            const host = wrapRef.current;
            dragRef.current = {
                mode: 'pan',
                x: e.clientX,
                y: e.clientY,
                left: host.scrollLeft,
                top: host.scrollTop,
            };
            return;
        }
        const p = pointAt(e);
        const reach = (HANDLE * 2.4) / box.scale;

        const current = selections.find((s) => s.id === selectedId);
        if (current) {
            const [kx, ky] = knobPoint(current, box.scale);
            if (Math.hypot(p.x - kx, p.y - ky) <= reach) {
                dragRef.current = { mode: 'rotate', id: current.id };
                return;
            }
            const pts = selectionCorners(current);
            for (let k = 0; k < 4; k += 1) {
                if (Math.hypot(p.x - pts[k][0], p.y - pts[k][1]) <= reach) {
                    dragRef.current = { mode: 'resize', id: current.id, corner: k, from: { ...current } };
                    return;
                }
            }
        }

        const inside = selections
            .filter((s) => pointInside(s, p))
            .sort((a, b) => a.w * a.h - b.w * b.h)[0];
        if (inside) {
            onSelect(inside.id);
            dragRef.current = { mode: 'move', id: inside.id, start: p, from: { ...inside } };
            return;
        }
        dragRef.current = { mode: 'draw', start: p };
        setDraft({ x: p.x, y: p.y, w: 0, h: 0 });
    };

    const onPointerMove = (e) => {
        const drag = dragRef.current;
        if (!drag) {
            setHover(panMode ? null : whatIsUnder(selections, selectedId, pointAt(e), box.scale));
            return;
        }
        if (drag.mode === 'pan') {
            const host = wrapRef.current;
            host.scrollLeft = drag.left - (e.clientX - drag.x);
            host.scrollTop = drag.top - (e.clientY - drag.y);
            return;
        }
        const p = pointAt(e);

        if (drag.mode === 'draw') { setDraft(rectFromDrag(drag.start, p)); return; }

        const target = selections.find((s) => s.id === drag.id);
        if (!target) return;

        if (drag.mode === 'move') {
            const moved = clampRect({
                ...drag.from,
                x: drag.from.x + (p.x - drag.start.x),
                y: drag.from.y + (p.y - drag.start.y),
            }, NW, NH);
            onChange(selections.map((s) => (s.id === drag.id ? moved : s)));
            return;
        }

        if (drag.mode === 'rotate') {
            const cx = target.x + target.w / 2;
            const cy = target.y + target.h / 2;
            // The knob points at twelve o'clock when the box is upright, so the
            // angle is measured from straight up rather than from the x axis.
            let deg = (Math.atan2(p.y - cy, p.x - cx) * 180) / Math.PI + 90;
            deg = ((deg + 180) % 360) - 180;
            // A shift-drag snaps, for the common case of "it is nearly level".
            if (e.shiftKey) deg = Math.round(deg / 15) * 15;
            onChange(selections.map((s) => (s.id === drag.id ? { ...s, rotation: Math.round(deg) } : s)));
            return;
        }

        // Resize a TURNED box in its own frame: the corner opposite the one
        // being dragged stays where it is, and the box keeps its angle.
        const { from, corner } = drag;
        const rad = ((from.rotation || 0) * Math.PI) / 180;
        const cx = from.x + from.w / 2;
        const cy = from.y + from.h / 2;
        const toLocal = ([x, y]) => {
            const dx = x - cx;
            const dy = y - cy;
            return [dx * Math.cos(-rad) - dy * Math.sin(-rad), dx * Math.sin(-rad) + dy * Math.cos(-rad)];
        };
        const [lx, ly] = toLocal([p.x, p.y]);
        const fixed = [
            [from.w / 2, from.h / 2], [-from.w / 2, from.h / 2],
            [-from.w / 2, -from.h / 2], [from.w / 2, -from.h / 2],
        ][corner];
        const w = Math.max(8, Math.abs(lx - fixed[0]));
        const h = Math.max(8, Math.abs(ly - fixed[1]));
        // The new centre is the midpoint of the fixed corner and the dragged
        // one, carried back out of the box's frame.
        const mid = [(lx + fixed[0]) / 2, (ly + fixed[1]) / 2];
        const ncx = cx + mid[0] * Math.cos(rad) - mid[1] * Math.sin(rad);
        const ncy = cy + mid[0] * Math.sin(rad) + mid[1] * Math.cos(rad);
        onChange(selections.map((s) => (s.id === drag.id
            ? { ...s, x: ncx - w / 2, y: ncy - h / 2, w, h }
            : s)));
    };

    const onPointerLeave = () => setHover(null);

    const endDrag = () => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (drag?.mode !== 'draw') { setDraft(null); return; }
        const rect = draft;
        setDraft(null);
        if (!rect || rect.w * box.scale < MIN_DRAG || rect.h * box.scale < MIN_DRAG) {
            onSelect(null);
            return;
        }
        const made = clampRect({ ...rect, id: newSelectionId(), rotation: 0 }, NW, NH);
        onChange([...selections, made]);
        onSelect(made.id);
    };

    return (
        /* A bounded window on to the picture. Without the cap the box grows
           with the canvas, so magnifying scrolls the PAGE and there is never
           anything to pan inside. */
        <div
            ref={wrapRef}
            className="w-full overflow-auto [scrollbar-width:thin]"
            style={{ maxHeight: VIEWPORT_MAX }}
        >
            <canvas
                ref={canvasRef}
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={endDrag}
                onPointerCancel={endDrag}
                onPointerLeave={onPointerLeave}
                style={{
                    width: box.width,
                    height: box.height,
                    touchAction: 'none',
                    cursor: panMode ? 'grab' : (hover || 'crosshair'),
                }}
                className={`mx-auto block rounded-xl ${panMode ? 'active:cursor-grabbing' : ''}`}
            />
        </div>
    );
}

/** Where the turn knob sits, in image pixels. */
function knobPoint(sel, scale) {
    const rad = ((sel.rotation || 0) * Math.PI) / 180;
    const cx = sel.x + sel.w / 2;
    const cy = sel.y + sel.h / 2;
    const up = sel.h / 2 + KNOB_LIFT / scale;
    return [cx + Math.sin(rad) * up, cy - Math.cos(rad) * up];
}

/**
 * The cursor for whatever is under the pointer.
 *
 * Deliberately asks the same questions in the same order as `onPointerDown`, so
 * the shape under the cursor is always the thing a click would actually grab.
 */
function whatIsUnder(selections, selectedId, p, scale) {
    const reach = (HANDLE * 2.4) / scale;
    const current = selections.find((s) => s.id === selectedId);
    if (current) {
        const [kx, ky] = knobPoint(current, scale);
        if (Math.hypot(p.x - kx, p.y - ky) <= reach) return 'grab';
        const pts = selectionCorners(current);
        const cx = current.x + current.w / 2;
        const cy = current.y + current.h / 2;
        for (const [x, y] of pts) {
            if (Math.hypot(p.x - x, p.y - y) <= reach) return resizeCursor(x - cx, y - cy);
        }
    }
    if (selections.some((sel) => pointInside(sel, p))) return 'move';
    return 'crosshair';
}

/**
 * The resize cursor that points along a corner, whatever angle the box is at.
 *
 * A turned box's top-left corner is not up and to the left any more, and a
 * fixed `nwse-resize` on it points the wrong way — so the direction is measured
 * and rounded to the nearest of the four arrows.
 */
function resizeCursor(dx, dy) {
    const deg = ((Math.atan2(dy, dx) * 180) / Math.PI % 180 + 180) % 180;
    return ['ew-resize', 'nwse-resize', 'ns-resize', 'nesw-resize'][Math.round(deg / 45) % 4];
}

/** Whether a point is on any selection, or on the selected one's handles. */
function hitsAnything(selections, p, scale) {
    const reach = (HANDLE * 2.4) / scale;
    for (const sel of selections) {
        if (pointInside(sel, p)) return true;
        for (const [x, y] of selectionCorners(sel)) {
            if (Math.hypot(p.x - x, p.y - y) <= reach) return true;
        }
        const [kx, ky] = knobPoint(sel, scale);
        if (Math.hypot(p.x - kx, p.y - ky) <= reach) return true;
    }
    return false;
}

/** Point-in-selection, done in the selection's own frame so a turn counts. */
function pointInside(sel, p) {
    const rad = ((sel.rotation || 0) * Math.PI) / 180;
    const cx = sel.x + sel.w / 2;
    const cy = sel.y + sel.h / 2;
    const dx = p.x - cx;
    const dy = p.y - cy;
    const lx = dx * Math.cos(-rad) - dy * Math.sin(-rad);
    const ly = dx * Math.sin(-rad) + dy * Math.cos(-rad);
    return Math.abs(lx) <= sel.w / 2 && Math.abs(ly) <= sel.h / 2;
}
